import { useCallback, useEffect, useRef, useState } from "react"
import {
  Alert,
  Box,
  Container,
  Snackbar,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import { flushSync } from "react-dom"
import { FormProvider, useForm } from "react-hook-form"
import { useNavigate, useParams } from "react-router-dom"
import { TSale, TSaleResult } from "../../core/types/Sale"
import OrderController, { montarPhonePayload } from "../../core/controllers/OrderController"
import {
  ICalculateInstallment,
  IOrderCharge,
  IOrderChargeStatus,
  IOrderResponse,
  TWalletAuthorizedResult,
} from "../../core/types/Order"
import {
  maskCepNumber,
  maskCnpjNumber,
  maskCpfNumber,
  numberOnly,
} from "../../masks/masks"
import { formatAsYouType, isValidPhone, ensurePhoneEngine } from "../../core/phone"
import {
  initialCheckoutList,
  isWidget,
  paymentsFailed,
  paymentsPaid,
} from "../../core/constants"
import CepController from "../../core/controllers/CepController"
import { IOrderBump } from "../../core/types/OrderBump"
import {
  IProductPrice,
  IProductPricePaymentInstallment,
  TPaymentMethodEnum,
  TPriceAffiliated,
  TProductLinkAffiliatedCookie,
} from "../../core/types/Product"
import { IErrorResponse } from "../../core/types/ErrorResponse"
import React from "react"
import TagFunctions, { ITagsProp } from "../../core/functions/TagFunctions"
import Validations from "../../core/functions/validations"
import { validateCvv } from "../../core/functions/CvvValidation"
import ProductController from "../../core/controllers/ProductController"
import OrderBumpController from "../../core/controllers/OrderBumpController"
import LostSaleController from "../../core/controllers/LostSaleController"
import { useQuery } from "../../components/Query/useQuery"
import { useLocalStorage } from "../../core/hooks/useLocalStorage"
import Convertions from "../../core/functions/convertions"
import InfoDialog from "../../components/Dialogs/InfoDialog"
// 3DS DESABILITADO TEMPORARIAMENTE — negociação administrativa pendente. Reativar descomentando os blocos marcados.
/*
import ThreeDSDialog from "../../components/Dialogs/ThreeDSDialog"
import ThreeDSController from "../../core/controllers/ThreeDSController"
import {
  isAuthenticated,
  hasFailed,
  mapTDSResultToOrderPayload,
  loadStone3DSSDK,
} from "../../core/functions/ThreeDSFunctions"
import { ChallengeWindowSize } from "../../core/types/ThreeDS"
import type { TDSOrderData } from "../../core/types/ThreeDS"
*/
import TermsPurchaseDialog from "./TermsPurchase"
import LoadingBackdrop from "../../components/Loading/LoadingBackdrop"
import CheckoutSendingBackdrop from "../../components/Checkout/CheckoutSendingBackdrop"
import {
  TCheckoutComponent,
  TCheckoutInstallmentsContext,
  TCheckoutLayout,
  TCheckoutPagePath,
} from "../../core/types/Checkout"
import CheckoutFunctions from "../../core/functions/CheckoutFunctions"
import Hosts from "../../core/functions/HostFunctions"
import MetaFunctions, { normalizePhoneForMeta } from "../../core/functions/MetaFunctions"
import KondutoFunctions from "../../core/functions/KondutoFunctions"
import Cookies from "js-cookie"
import CheckoutComponenetList from "../../components/CustomCheckout/CheckoutComponentList"
import GoogleFunctions from "../../core/functions/GoogleFunctions"
import { diffHours } from "../../core/functions/dateTime"
import HostFunctions from "../../core/functions/HostFunctions"
import VoucherController from "../../core/controllers/VoucherController"
import { TProductVoucherValue } from "../../core/types/Voucher"
import NumberFunctions from "../../core/functions/NumberFunctions"
import ErrorSnackbar from "../../components/Snackbar/ErrorSnackbar"
import { useGoogleReCaptcha } from "react-google-recaptcha-v3"
import "./Checkout.css"
import {
  TCustomCheckoutComponentDevice,
  TModelType,
  TPopupConfig,
} from "../../core/types/CustomCheckout"
import ExitPopupDialog from "./ExitPopup"
import CheckoutPageFunctions from "./CheckoutPageFunctions"
import { getResponseError } from "../../services/api"
import CheckoutConsts from "./CheckoutConsts"
import CheckoutLoading from "./CheckoutLoading"
import CheckoutError from "./CheckoutError"
import {
  EVENT_LISTENER_TYPE_UNHANDLED_REJECTION,
  analyticsPageView,
  onErrorAnalytics,
  onEventListenerUnhandledRejection,
} from "../../services/firebaseConfig"
import PaymentFunctions from "./Payment/PaymentFunctions"
import { useCheckoutContext } from "../../core/hooks/useCheckoutContext"
import useDeviceFingerprint from "../../core/hooks/useDeviceFingerprint"
import { GatewayProvider } from "../../core/contexts/GatewayContext"
import { DEFAULT_BUYER_COUNTRY, detectBuyerCountry } from "../../core/functions/BuyerCountryFunctions"
import { IStripeCardFormRef } from "../../core/types/Stripe"
import { useTranslation } from "react-i18next"
import useRevalidateOnLanguageChange from "../../core/i18n/useRevalidateOnLanguageChange"

/**
 * Componente responsável por gerenciar a página do checkout.
 */
const CheckoutPage: React.FC = () => {
  let { codeId, checkoutOrderCodeId, subscriptionCodeId, areaCodeId } =
    useParams<TCheckoutPagePath>()

  if (!codeId || codeId === "") {
    const widgetCodeId = localStorage.getItem("codeId")
    if (widgetCodeId) {
      codeId = widgetCodeId
    }
  }

  const {
    checkoutSettings,
    setCheckoutSettings,
    productPrice: data,
    setProductPrice: setData,
    installment,
    isPurposeCardUpdate,
    setInstallment,
    setPurpose,
    setSendingPurchase,
  } = useCheckoutContext()

  const sessionId = useRef<string>(
    Math.floor(new Date().getTime() / 1000).toString(),
  )
  const fingerprintRef = useDeviceFingerprint(data?.fingerprintEnabled ?? false)
  const tagFacebook = useRef<ITagsProp | null>(null)
  const tagGoogleAds = useRef<ITagsProp[] | null>(null)
  const tagInitialDataLayer = useRef<ITagsProp | null>(null)
  const tagDataLayer = useRef<ITagsProp | null>(null)
  const tagGTM = useRef<ITagsProp | null>(null)
  const metasKonduto = useRef<HTMLMetaElement[]>([])
  const productLinkAffiliated = useRef<
    TProductLinkAffiliatedCookie | undefined
  >()
  const isPreviewedExitPopup = useRef(false)
  const voucherType = useRef<"voucher" | "discount">("voucher")

  const query = useQuery()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [sending, setSending] = useState(false)
  const [success, setSuccess] = useState(false)
  const [viewLoading, setViewLoading] = useState(false)
  const [waitForApproval, setWaitForApproval] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingVoucher, setLoadingVoucher] = useState(false)
  const [searchedZipCode, setSearchedZipCode] = useState(false)
  const [openZipCodeInvalid, setOpenZipCodeInvalid] = useState(false)
  const [openTermsPurchase, setOpenTermsPurchase] = useState(false)
  const [successVoucher, setSuccessVoucher] = useState(false)
  const [isExitPopup, setIsExitPopup] = useState(false)
  // 3DS DESABILITADO TEMPORARIAMENTE — negociação administrativa pendente. Reativar descomentando os blocos marcados.
  // const [threeDSOpen, setThreeDSOpen] = useState(false)

  const [errorVoucher, setErrorVoucher] = useState("")

  const [affiliated, setAffiliated] = useState<TPriceAffiliated | undefined>(
    undefined,
  )
  const [dataResult, setDataResult] = useState<IOrderResponse | undefined>(
    undefined,
  )
  const [chargeId, setChargeId] = useState<string | undefined>(undefined)
  const [checkout, setCheckout] = useState<TSaleResult | undefined>(undefined)

  const [orderBumps, setOrderBumps] = useState<IOrderBump[]>([])

  const [list, setList] = useState([...initialCheckoutList])
  const [exitPopup, setExitPopup] = useState<TPopupConfig | undefined>()

  const [error, setError] = useState("")
  const [errorPrice, setErrorPrice] = useState("")

  const [, setSaleStorage] = useLocalStorage("afpa:sale", null)

  const onlyProducer = window.location.pathname.startsWith("/pro/")
  const isTicket = window.location.pathname.startsWith("/t/")

  const isNewLayoutCoverImage = CheckoutFunctions.isNewLayoutCoverImage(
    data?.checkoutComponents,
  )

  const paymentMethodDefault =
    CheckoutFunctions.getModelByParams(query) === TCheckoutLayout.Layout8
      ? TPaymentMethodEnum.Pix
      : TPaymentMethodEnum.CreditCard

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))
  const isLayout6 = CheckoutFunctions.isLayout6ModelF(data?.checkoutComponents)
  const isLayout7 = CheckoutFunctions.isLayout7ModelG(data?.checkoutComponents)
  const isLayout8 = CheckoutFunctions.isLayout8ModelH(data?.checkoutComponents)

  const methods = useForm<TSale>({
    defaultValues: CheckoutFunctions.getSaleDefault(
      query,
      data?.id,
      onlyProducer,
      subscriptionCodeId,
      areaCodeId,
      paymentMethodDefault,
    ),
    mode: "onBlur",
    reValidateMode: "onChange",
  })

  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
    getValues,
    setValue,
    setFocus,
    clearErrors,
  } = methods

  useRevalidateOnLanguageChange(methods)

  const { executeRecaptcha } = useGoogleReCaptcha()

  const checkoutOrderIdValue = watch("checkoutOrderId")
  const nameValue = watch("name")
  const emailValue = watch("email")
  const emailConfirmValue = watch("emailConfirm")
  const phoneValue = watch("phone")
  const phoneCountryValue = watch("phoneCountry") ?? "BR"
  const documentValue = watch("document")
  const zipCodeValue = watch("address.zipCode")
  const paymentMethodType = watch("payment.methodType")
  const paymentInstallment = watch("payment.installment")
  const paymentTotal = watch("payment.total")
  const paymentValue = watch("payment.value")
  const voucherIdentifier = watch("payment.voucherIdentifier")
  const voucherValueType = watch("payment.voucherType")
  const voucherValue = watch("payment.voucherValue")
  const voucherStatus = watch("payment.voucherStatus")
  const products = watch("products")
  const pageAccessIdValue = watch("pageAccessId")
  const clientIpValue = watch("clientIP")
  const quantityValue = watch("quantity")
  const areaCodeIdValue = watch("ticketSectionCodeId")

  const fieldSetFocus = useRef(false)
  const id = useRef("")
  const searched = useRef(false)
  const disableFocus = useRef(false)
  const lostSaleCodeId = useRef<string>()
  const lostSaleSending = useRef(false)
  const accessPageDate = useRef<Date | null>(null)
  const verifiedIntervalValue = useRef(5000)
  const stripeFormRef = useRef<IStripeCardFormRef>(null)
  const [buyerCountry, setBuyerCountryState] = useState(DEFAULT_BUYER_COUNTRY)
  // Escolha manual no seletor vence a detecção por IP, mesmo se ela responder depois.
  const buyerCountryTouched = useRef(false)
  const setBuyerCountry = useCallback((country: string) => {
    buyerCountryTouched.current = true
    setBuyerCountryState(country)
  }, [])
  // Só oferta com venda internacional consulta o país pelo IP; as outras ficam em BR.
  // Roda antes de sair da tela de carregamento, para os meios de pagamento não
  // trocarem na frente do comprador.
  const applyDetectedBuyerCountry = useCallback(
    async (productPrice?: IProductPrice) => {
      if (!productPrice?.internationalSalesEnabled) return
      const country = await detectBuyerCountry()
      if (!buyerCountryTouched.current) setBuyerCountryState(country)
    },
    []
  )
  const [stripeConfigError, setStripeConfigError] = useState(false)
  const useStripePayment = buyerCountry.toUpperCase() !== 'BR' && (data?.internationalSalesEnabled ?? false)

  let verifiedCheckoutChargeInterval: NodeJS.Timeout | null = null

  const getClientIp = useCallback(async () => {
    if ((clientIpValue ?? "") === "") {
      const ipAddress = await HostFunctions.getIpAddress()
      setValue("clientIP", ipAddress)
      return ipAddress
    }
    return clientIpValue ?? ""
  }, [clientIpValue, setValue])

  const updatePaymentValues = useCallback(() => {
    const { value, subtotal, discount, total } =
      CheckoutPageFunctions.calcInstallmentValues(
        voucherStatus,
        voucherValue,
        voucherValueType,
        data,
        products,
        quantityValue,
      )

    setValue("payment.value", value)
    setValue("payment.subtotal", subtotal)
    setValue("payment.discount", discount)
    setValue("payment.total", total)
  }, [
    data,
    products,
    voucherStatus,
    voucherValueType,
    voucherValue,
    quantityValue,
    setValue,
  ])

  const handleLoadCalculateInstallments = useCallback(
    async (
      newValue: number,
      mainOnly: boolean,
      installment: TCheckoutInstallmentsContext | undefined,
    ) => {
      if ((installment?.main ?? []).length <= 0) {
        return
      }

      setSending(true)
      try {
        const response = await OrderController.calculeIsntallments({
          priceId: id.current,
          newValue,
        })
        const responseError = response as IErrorResponse
        const responseData = response as ICalculateInstallment

        if (responseError.code) {
          setError(responseError.error)
        } else {
          if (mainOnly) {
            const respOriginal = await OrderController.calculeIsntallments({
              priceId: id.current,
              newValue: quantityValue * (data?.value ?? 0),
            })
            const originData = respOriginal as ICalculateInstallment
            if (originData.installments) {
              setInstallment({
                main: responseData.installments,
                checkout: responseData.installments,
                original: originData.installments,
              })
            }
          } else {
            setInstallment({
              main: installment?.main ?? responseData.installments,
              checkout: responseData.installments,
              original: installment?.original,
            })
          }

          updatePaymentValues()
        }
      } finally {
        setSending(false)
      }
    },
    [paymentInstallment, updatePaymentValues],
  )

  const orderBumpChange = useCallback(
    async (orderBump: IOrderBump) => {
      const currentProducts = getValues("products")
      const { error, newValue, newProducts } =
        await CheckoutPageFunctions.addOrRemoveOrderBumps(
          orderBump,
          data,
          installment,
          id.current,
          currentProducts,
        )

      if (error !== "") {
        setError(error)
        return
      }

      setValue("products", newProducts, {
        shouldDirty: true,
        shouldValidate: false,
      })
      await handleLoadCalculateInstallments(newValue, false, installment)
    },
    [
      data?.value,
      handleLoadCalculateInstallments,
      products,
      setValue,
      installment,
    ],
  )

  const updateCustomCheckoutSettings = useCallback((device?: any) => {
    if (device && device !== "null") {
      const obj = JSON.parse(device) as TCustomCheckoutComponentDevice
      setCheckoutSettings(obj.settings)
      setList(obj.components)
      setExitPopup(obj.popupConfig)
    }
  }, [])

  const updateCustomCheckout = useCallback(
    (price: IProductPrice) => {
      const customCheckout = price.customCheckout
      if (customCheckout) {
        const deviceLayout = isMobile
          ? customCheckout.mobileLayout
          : customCheckout.desktopLayout
        updateCustomCheckoutSettings(deviceLayout)
      }
    },
    [isMobile, updateCustomCheckoutSettings],
  )

  useEffect(() => {
    if (searched && checkout) {
      const { address, products: checkoutProducts } = checkout

      setValue("checkoutOrderId", checkout.checkoutOrderId)
      setValue("productPriceId", checkout.productPriceId)
      setValue("name", checkout.name)
      setValue("document", checkout.document)
      setValue("email", checkout.email)
      setValue("emailConfirm", checkout.email)

      // O backend devolve os campos separados desde a Fase 1. "phone" é o
      // valor bruto do banco, mantido por retrocompatibilidade e não
      // garantido limpo em registro não migrado — "phoneNational" é o
      // campo desenhado para ser confiável e deve ser usado aqui.
      // countryCode nulo (registro não migrado) cai em BR, que é o
      // comportamento correto para toda a base histórica.
      const iso = checkout.countryCode || "BR"
      const nacional = checkout.phoneNational ?? checkout.phone ?? ""
      ensurePhoneEngine(iso).then(() => {
        setValue("phoneCountry", iso)
        setValue("phone", formatAsYouType(nacional, iso))
      })

      setTimeout(() => {
        setValue("address.city", address.city)
        setValue("address.neighborhood", address.neighborhood)
        setValue("address.street", address.street)
        setValue("address.number", address.number)
        setValue("address.state", address.state)
        setValue("address.complement", address.complement)

        disableFocus.current = false
      }, 2000)

      checkoutProducts.map((product) => {
        const orderBump = orderBumps.find(
          (ob) => ob.productPriceId === product.productPriceId,
        )
        if (orderBump) {
          orderBump.selected = true
          orderBumpChange(orderBump)
        }
        return true
      })

      setCheckout(undefined)
    }
  }, [checkout, setValue, setFocus, orderBumps, orderBumpChange])

  const sendPixelsInitiateCheckoutData = () => {
    //sendCheckoutPixels(data);
  }

  const sendPixelsInitiateCheckout = (
    productPrice: IProductPrice | null,
    priceAffiliated: TPriceAffiliated | undefined,
  ) => {
    if (productPrice && priceAffiliated && priceAffiliated.pixels.length > 0) {
      if (tagFacebook.current) {
        return
      }

      const { phone: phoneNacional, phoneCountryCode: ddiAtual } =
        montarPhonePayload(watch("phone"), watch("phoneCountry"))
      const fullPhone = normalizePhoneForMeta(phoneNacional, ddiAtual)

      try {
        tagFacebook.current = MetaFunctions.addTagInitiateCheckout(
          sessionId.current,
          productPrice.codeId ?? "",
          productPrice.productCode ?? "",
          productPrice.productName ?? "",
          productPrice.value ?? 0.0,
          priceAffiliated.pixels,
          emailValue,
          nameValue,
          fullPhone,
          documentValue,
          watch("address.city"),
          watch("address.state"),
          watch("address.zipCode"),
        )
      } catch (e) {
        console.error(e)
      }

      try {
        MetaFunctions.sendCheckoutAPI(
          sessionId.current,
          codeId,
          productPrice.productCode,
          productPrice.value ?? 0,
          priceAffiliated.pixels,
          emailValue,
          nameValue,
          fullPhone,
          documentValue,
          watch("address.city"),
          watch("address.state"),
          watch("address.zipCode"),
        )
      } catch (e) {
        console.error(e)
      }
    }
  }

  const sendPixelsAddPaymentInfo = (
    productPrice: IProductPrice | null,
    priceAffiliated: TPriceAffiliated | undefined,
  ) => {
    if (productPrice && priceAffiliated && priceAffiliated.pixels.length > 0) {
      const addPaymentInfoId = Math.floor(
        new Date().getTime() / 1000,
      ).toString()

      const { phone: phoneNacional, phoneCountryCode: ddiAtual } =
        montarPhonePayload(watch("phone"), watch("phoneCountry"))
      const fullPhone = normalizePhoneForMeta(phoneNacional, ddiAtual)

      CheckoutFunctions.setUserCookie(
        emailValue,
        nameValue,
        fullPhone,
        documentValue,
        watch("address.city"),
        watch("address.state"),
        watch("address.zipCode"),
      )

      try {
        tagFacebook.current = MetaFunctions.addTagAddPaymentInfo(
          addPaymentInfoId,
          productPrice.codeId ?? "",
          productPrice.productCode ?? "",
          productPrice.productName ?? "",
          productPrice.value ?? 0.0,
          Convertions.methodTypeToPaymentMethod(paymentMethodType),
          priceAffiliated.pixels,
          emailValue,
          nameValue,
          fullPhone,
          documentValue,
          watch("address.city"),
          watch("address.state"),
          watch("address.zipCode"),
          undefined,
          watch("payment.total"),
        )
      } catch (e) {
        console.error(e)
      }

      try {
        MetaFunctions.sendAddPaymentInfoAPI(
          addPaymentInfoId,
          codeId,
          productPrice.productCode,
          productPrice.value ?? 0,
          Convertions.methodTypeToPaymentMethod(paymentMethodType),
          priceAffiliated.pixels,
          emailValue,
          nameValue,
          fullPhone,
          documentValue,
          watch("address.city"),
          watch("address.state"),
          watch("address.zipCode"),
          undefined,
          watch("payment.total"),
        )
      } catch (e) {
        console.error(e)
      }
    }
  }

  const registerAccess = async (
    price: IProductPrice | null,
    lostSaleCode: string | undefined,
  ) => {
    if (price) {
      try {
        const clientIp = await getClientIp()
        const afid = query.get("afid")
        const fbclid = query.get("fbclid")
        const access = CheckoutPageFunctions.getPageAccess(
          clientIp,
          price,
          codeId,
          afid,
          lostSaleCode,
          fbclid,
        )
        const response = await OrderController.registerAccess({
          data: access,
        })
        setValue("pageAccessId", response.data?.id)
        setValue("sessionToken", response.data?.token)
      } catch (e) {
        console.error(e)
      }
    }
  }

  const getOrderBumpInstallments = useCallback(
    async (orderBump: IOrderBump) => {
      const orderBumpValue =
        orderBump.productPrice?.firstPurchasePrice ??
        orderBump.productPrice?.value ??
        0
      var installments: IProductPricePaymentInstallment[] = []

      const response = await OrderController.calculeIsntallments({
        priceId: id.current,
        newValue: orderBumpValue,
      })
      const responseError = response as IErrorResponse
      const responseData = response as ICalculateInstallment

      if (!responseError.code) {
        installments = responseData.installments
      }

      orderBump.installments = installments
      return orderBump
    },
    [installment],
  )

  const getOrderBumpInstallmentsAll = useCallback(
    async (list: IOrderBump[]) => {
      const newOrderBumps: IOrderBump[] = []

      for (var i = 0; i < list.length; i++) {
        const order = await getOrderBumpInstallments(list[i])
        newOrderBumps.push({ ...order })
      }
      setOrderBumps([...newOrderBumps])
    },
    [orderBumps, setOrderBumps, getOrderBumpInstallments],
  )

  const getOrderBumps = useCallback(
    async (code?: string): Promise<void> => {
      try {
        if (code && code !== "") {
          const responseOrder = await OrderBumpController.getByProductPriceId({
            productPriceId: id.current,
          })
          const responseErrorOrder = responseOrder as IErrorResponse
          const responseDataOrder = responseOrder as IOrderBump[]

          if (responseErrorOrder.code) {
            setError(responseErrorOrder.error)
          } else {
            getOrderBumpInstallmentsAll(responseDataOrder)
          }
        }
      } catch (e) {
        console.error(e)
        const message = getResponseError(e)
        setError(message)
      }
    },
    [id, setError, setOrderBumps],
  )

  const getAffiliated = useCallback(
    async (code?: string): Promise<void> => {
      if (code && code !== "") {
        try {
          let afid = query.get("afid")
          let plid = query.get("pa")
          let afcd: Date | null | undefined

          if (!afid || afid === "") {
            const cookieOther = Cookies.get("__apop")
            if (cookieOther) {
              try {
                productLinkAffiliated.current = JSON.parse(
                  cookieOther,
                ) as TProductLinkAffiliatedCookie

                const linkCreated = new Date(productLinkAffiliated.current.cre)
                const diffInHours = diffHours(new Date(), linkCreated)

                if (diffInHours <= 1 && productLinkAffiliated.current) {
                  afid = productLinkAffiliated.current.afid
                  afcd = linkCreated
                }
              } catch (e) {}
            }
          }

          if (!afid || afid === "") {
            const cookieValue = Cookies.get(`_ap_${code}`)
            if (cookieValue) {
              try {
                productLinkAffiliated.current = JSON.parse(
                  cookieValue,
                ) as TProductLinkAffiliatedCookie
                if (productLinkAffiliated.current) {
                  afid = productLinkAffiliated.current.afid
                  afcd = new Date(productLinkAffiliated.current.cre)
                }
              } catch (e) {}
            }
          }

          const clientIp = await getClientIp()
          const response = await ProductController.getPriceAffiliatedByCode({
            priceCode: code,
            afid,
            plid,
            afcd,
            clientIp,
            onlyProducer,
          })
          const responseError = response as IErrorResponse
          const responseData = response as TPriceAffiliated

          if (!responseError.code) {
            setAffiliated(responseData)
          }
        } catch (e) {
          console.error(e)
        }
      }
    },
    [productLinkAffiliated, setData],
  )

  const getCheckoutExists = useCallback(
    async (
      checkoutCode?: string,
      subscriptionCode?: string,
      areaCode?: string,
    ): Promise<void> => {
      const isCheckout = checkoutCode && checkoutCode !== ""
      const isSubscription = subscriptionCode && subscriptionCode !== ""

      if (isCheckout || isSubscription) {
        const checkoutPromise = isCheckout
          ? OrderController.getCheckoutOrder({
              checkoutOrderCodeId: checkoutCode,
              afid: query.get("afid"),
            })
          : OrderController.getCheckoutSubscription({
              subscriptionCodeId: subscriptionCode,
            })

        const checkout = await checkoutPromise
        setCheckout(checkout)

        if (checkout) {
          const { address, productPrice, orderBumps: bumps } = checkout

          codeId = productPrice?.codeId || ""
          id.current = productPrice?.id || ""
          disableFocus.current = true

          if (productPrice) {
            await applyDetectedBuyerCountry(productPrice)
            setData(productPrice)
            await getAffiliated(codeId)
            await getOrderBumps(codeId)
          }

          setOrderBumps(bumps)
          setValue("address.zipCode", address.zipCode)
          await registerAccess(productPrice, lostSaleCodeId.current)
        }
      }
    },
    [
      codeId,
      id,
      disableFocus,
      query,
      lostSaleCodeId,
      setCheckout,
      setData,
      getAffiliated,
      getOrderBumps,
      setOrderBumps,
      setValue,
      registerAccess,
    ],
  )

  const getProductPrice = useCallback(
    async (
      code: string | undefined,
      checkoutCode: string | undefined,
      subscriptionCode: string | undefined,
      areaCode: string | undefined,
    ): Promise<void> => {
      if (Hosts.isHostGoAffiliatesPay()) {
        return
      }

      if (searched.current) {
        return
      }

      searched.current = true
      try {
        setLoading(true)

        CheckoutPageFunctions.identifyFacebookPix(query)

        if (error !== "") {
          setErrorPrice("")
        }

        await getCheckoutExists(checkoutCode, subscriptionCode)

        const isProductPrice = code && code !== ""
        const isProductTicket = areaCode && areaCode !== ""
        if (isProductPrice || isProductTicket) {
          const model = CheckoutFunctions.getModelByParams(query)

          const request = isProductPrice
            ? ProductController.getPaymentByPrice({
                priceCode: code,
                model,
              })
            : ProductController.getProductPriceByArea({
                areaCodeId: areaCode || "",
                model,
              })
          const response = await request
          const responseError = response as IErrorResponse
          if (responseError.code) {
            setErrorPrice(responseError.error)
          } else {
            const responseData = response as IProductPrice
            id.current = responseData.id
            setValue("ticketSectionId", responseData.ticketSectionId)
            setValue("ticketTierCodeId", responseData.ticketTierCodeId)
            setValue("ticketTierId", responseData.ticketTierId)
            setValue("ticketTierCodeId", responseData.ticketTierCodeId)
            await applyDetectedBuyerCountry(responseData)
            setData(responseData)
            setLoading(false)
            await getAffiliated(responseData.codeId)
            await getOrderBumps(responseData.codeId)
            await registerAccess(responseData, lostSaleCodeId.current)
          }
        }
      } catch (e) {
        console.error(e)
        const message = getResponseError(e)
        console.error(message)
      } finally {
        if (loading) setLoading(false)
        setTimeout(() => {
          searched.current = false
        }, 1000)
      }
    },
    [
      id,
      query,
      error,
      searched,
      productLinkAffiliated,
      diffHours,
      getAffiliated,
      getOrderBumps,
      setLoading,
      setErrorPrice,
      setCheckout,
      setData,
      setOrderBumps,
      setValue,
      registerAccess,
    ],
  )

  const searchZipCode = useCallback(
    async (load: boolean, zipcode?: string) => {
      if (!zipcode || load) {
        return
      }
      const code = numberOnly(zipcode)
      if (code.length < 8) {
        return
      }
      setSending(true)
      try {
        const cep = await CepController.search(code)
        if (cep) {
          setValue("address.street", cep.logradouro)
          setValue("address.complement", cep.complemento)
          setValue("address.neighborhood", cep.bairro)
          setValue("address.city", cep.localidade)
          setValue("address.state", cep.uf)

          if (!disableFocus.current) {
            setTimeout(() => {
              if (!disableFocus.current) {
                if (cep.logradouro === "") {
                  setFocus("address.street", {
                    shouldSelect: true,
                  })
                } else {
                  setFocus("address.number", {
                    shouldSelect: true,
                  })
                }
              }
            }, 1000)
          }
        } else {
          setOpenZipCodeInvalid(true)
        }
      } finally {
        setSending(false)
        setSearchedZipCode(true)
      }
    },
    [setFocus, setValue, setOpenZipCodeInvalid],
  )

  const verifiedCheckoutChargeSuccess = useCallback(
    (charge: IOrderCharge, path: "thanks" | "success") => {
      setWaitForApproval(false)
      setSuccess(true)
      setViewLoading(false)
      setSendingPurchase(false)

      const code = codeId || data?.codeId || ""
      const sale: TSale = watch() as TSale
      const query = CheckoutFunctions.getQuerySuccessPage(
        code,
        data!,
        affiliated!,
        sale,
        dataResult!,
        onlyProducer,
        charge,
        orderBumps,
      )
      Hosts.openNavigate(`/${code}/${path}/redirect${query}`, navigate)
    },
    [
      codeId,
      data,
      affiliated,
      dataResult,
      onlyProducer,
      navigate,
      setWaitForApproval,
      setSuccess,
      setViewLoading,
      orderBumps,
    ],
  )

  const verifiedCheckoutCharge = useCallback(async () => {
    try {
      if (verifiedCheckoutChargeInterval) {
        try {
          clearInterval(verifiedCheckoutChargeInterval)
        } catch (e) {
          console.error(e)
        }
      }

      verifiedCheckoutChargeInterval = setInterval(async () => {
        if (waitForApproval) {
          try {
            const charge = await OrderController.verified({ chargeId })
            if (charge && charge.status !== IOrderChargeStatus.Processing) {
              if (paymentsFailed.includes(charge.status)) {
                setSaleStorage(null)
                setWaitForApproval(false)
                setViewLoading(false)
                setSendingPurchase(false)
                setError(
                  Convertions.getErrorPayment(paymentMethodType, {
                    success: false,
                    code: -1,
                    error: charge.error ?? "",
                  }),
                )
                if (verifiedCheckoutChargeInterval)
                  clearInterval(verifiedCheckoutChargeInterval)
              } else if (
                [TPaymentMethodEnum.Pix, TPaymentMethodEnum.Boleto].includes(
                  paymentMethodType,
                )
              ) {
                if (
                  charge.pixQrCode ||
                  charge.pixQrUrl ||
                  charge.boletoUrl ||
                  charge.boletoCodeBar ||
                  charge.boletoQrCode
                ) {
                  verifiedCheckoutChargeSuccess(charge, "thanks")
                  if (verifiedCheckoutChargeInterval)
                    clearInterval(verifiedCheckoutChargeInterval)
                }
              } else if (paymentsPaid.includes(charge.status)) {
                verifiedCheckoutChargeSuccess(charge, "success")
                if (verifiedCheckoutChargeInterval)
                  clearInterval(verifiedCheckoutChargeInterval)
              }
            }
          } catch (err) {
            console.error("verifiedCheckoutCharge", err)
            Promise.reject(
              err instanceof Error
                ? err
                : new Error(`Erro desconhecido: ${JSON.stringify(err)}`),
            )
            if (verifiedCheckoutChargeInterval)
              clearInterval(verifiedCheckoutChargeInterval)
          }
        }
      }, verifiedIntervalValue.current)

      if (verifiedIntervalValue.current >= 3000) {
        verifiedIntervalValue.current -= 1000
      }
    } catch (err) {
      console.error("verifiedCheckoutCharge", err)
      Promise.reject(
        err instanceof Error
          ? err
          : new Error(`Erro desconhecido: ${JSON.stringify(err)}`),
      )
      if (verifiedCheckoutChargeInterval)
        clearInterval(verifiedCheckoutChargeInterval)
    }
  }, [
    waitForApproval,
    chargeId,
    codeId,
    data,
    affiliated,
    dataResult,
    onlyProducer,
    watch,
    navigate,
    setSaleStorage,
    setWaitForApproval,
    setViewLoading,
    setError,
  ])

  const sendApplyVoucher = useCallback(
    async (identifier: string | undefined | null) => {
      if (identifier) {
        setLoadingVoucher(true)
        try {
          const response = await VoucherController.applyVoucher({
            productId: data?.productId ?? "",
            productPriceId: data?.id ?? "",
            voucherIdentifier: identifier,
          })
          if (response.success && response.data) {
            setValue("payment.voucherStatus", "valid")
            setValue("payment.voucherId", response.data.voucherId)
            setValue(
              "payment.voucherIdentifier",
              response.data.voucherIdentifier,
            )
            setValue("payment.voucherValue", response.data.value)
            setValue("payment.voucherType", response.data.valueType)
            setSuccessVoucher(true)
          } else {
            setValue("payment.voucherStatus", "invalid")
            setErrorVoucher(t("errors.invalidCoupon"))
          }
        } finally {
          setLoadingVoucher(false)
        }
      }
    },
    [data, setLoadingVoucher],
  )

  const applyVoucher = (voucherCode: string) => {
    voucherType.current = "discount"
    setValue("payment.voucherIdentifier", voucherCode)
    sendApplyVoucher(voucherCode)
  }

  const showExitPopupMobile = useCallback(() => {
    const isShowPreviewedExitPopup =
      isMobile &&
      exitPopup &&
      exitPopup.modelType !== TModelType.none &&
      !isPreviewedExitPopup.current

    if (isShowPreviewedExitPopup) {
      isPreviewedExitPopup.current = true
      setIsExitPopup(true)
    }
  }, [isMobile, exitPopup, isPreviewedExitPopup, setIsExitPopup])

  const handleMouseleave = (event: any) => {
    const mouseY = event.clientY
    if (mouseY < 0) {
      const isExitPopup = exitPopup && exitPopup.modelType !== TModelType.none
      const isShowPreviewedExitPopup =
        !isMobile && isExitPopup && !isPreviewedExitPopup.current

      if (isShowPreviewedExitPopup) {
        isPreviewedExitPopup.current = true
        setIsExitPopup(true)
      }
    }
  }

  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState === "hidden") {
      showExitPopupMobile()
    }
  }, [
    isMobile,
    exitPopup,
    isPreviewedExitPopup,
    setIsExitPopup,
    showExitPopupMobile,
  ])

  useEffect(() => {
    if (emailValue.includes(" ")) {
      const email = emailValue.replace(" ", "")
      setValue("email", email)
    }
  }, [emailValue, setValue])

  useEffect(() => {
    if (emailConfirmValue.includes(" ")) {
      const email = emailConfirmValue.replace(" ", "")
      setValue("emailConfirm", email)
    }
  }, [emailConfirmValue, setValue])

  useEffect(() => {
    const formatado = formatAsYouType(phoneValue, phoneCountryValue)
    setValue("phone", formatado, { shouldDirty: true, shouldValidate: true })

    // Avança o foco quando o número fica válido para o país selecionado.
    // O limiar antigo (length > 14) era o comprimento da máscara brasileira
    // e não fazia sentido nos demais países.
    if (formatado !== phoneValue && isValidPhone(formatado, phoneCountryValue)) {
      nextInput()
    }
  }, [phoneValue, phoneCountryValue, setValue])

  const nextInputCpfCnpj = () => {
    if (CheckoutFunctions.isLayout5ModelE(data?.checkoutComponents)) {
      setFocus("phone")
    } else {
      nextInput()
    }
  }

  useEffect(() => {
    const documentNumberOnly = numberOnly(documentValue)
    if (documentNumberOnly.length > 11) {
      const documentComplete = maskCnpjNumber(documentValue)
      setValue("document", documentComplete)

      if (documentNumberOnly.length > 13) {
        if (Validations.CNPJ(documentValue)) {
          nextInputCpfCnpj()
        }
      }
    } else {
      const documentComplete = maskCpfNumber(documentValue)
      setValue("document", documentComplete)

      if (documentNumberOnly.length > 10) {
        if (Validations.CPF(documentValue)) {
          nextInputCpfCnpj()
        }
      }
    }
  }, [documentValue, setValue])

  useEffect(() => {
    const zipcode = maskCepNumber(zipCodeValue)
    setValue("address.zipCode", zipcode)
    searchZipCode(loading, zipcode)
  }, [zipCodeValue, setValue, searchZipCode, loading])

  useEffect(() => {
    if (data) {
      try {
        if (!tagDataLayer.current) {
          tagDataLayer.current = GoogleFunctions.addDataLayerBeginCheckout(data)
        }
        if (metasKonduto.current.length <= 0) {
          metasKonduto.current = KondutoFunctions.addTagMetaCheckoutProduct(
            codeId || "",
            data?.productName || "",
          )
        }

        const voucherCode = CheckoutFunctions.getVoucherAuto(query)
        if (voucherCode) applyVoucher(voucherCode)
      } catch (e) {
        console.error(e)
      }
    }

    return () => {
      if (tagDataLayer.current) {
        TagFunctions.removeTags(tagDataLayer.current)
      }
      if (tagGTM.current) {
        TagFunctions.removeTags(tagGTM.current)
      }
    }
  }, [data])

  useEffect(() => {
    if (affiliated) {
      sendPixelsInitiateCheckout(data, affiliated)

      const isGoogleTagManagerProducer = (data?.googleTagManagerId ?? "") !== ""
      const isGoogleTagManagerAffiliated =
        (affiliated.googleTagManagerId ?? "") !== ""

      if (!tagGTM.current && isGoogleTagManagerAffiliated) {
        tagGTM.current = GoogleFunctions.addGoogleTagManager(
          affiliated.googleTagManagerId,
        )
      } else if (!tagGTM.current && isGoogleTagManagerProducer) {
        tagGTM.current = GoogleFunctions.addGoogleTagManager(
          data?.googleTagManagerId,
        )
      }

      if (!tagGoogleAds.current) {
        tagGoogleAds.current = GoogleFunctions.addTagGoogleAdsPixelCheckout(
          affiliated.pixels,
        )
      }

      const affiliateCodeValue = watch("affiliateCode")
      if (!affiliateCodeValue || affiliateCodeValue === "") {
        const isAffiliated =
          affiliated?.affiliated &&
          affiliated.affiliated.affiliatedCodeId !== ""
        if (isAffiliated) {
          setValue("affiliateCode", affiliated.affiliated?.affiliatedCodeId)
          CheckoutPageFunctions.setProductLinkAffiliatedCookie(
            productLinkAffiliated.current,
            affiliated.affiliated?.affiliatedCodeId ?? "",
            setValue,
          )
        }
      }
    }
  }, [data, affiliated])

  useEffect(() => {
    if (data) {
      if (!isWidget()) {
        document.title = data?.productName ?? "Checkout Celetus"
      }

      analyticsPageView()
      let installmentNumber = 1

      if (
        paymentMethodType === TPaymentMethodEnum.CreditCard &&
        data &&
        data.paymentMethods.length > 0
      ) {
        const id = data.paymentMethods[0].methodType

        if (id === TPaymentMethodEnum.CreditCard) {
          const payment = data?.paymentMethods.find(
            (x) => x.methodType === TPaymentMethodEnum.CreditCard,
          )
          if (payment) {
            setInstallment({
              main: installment?.main ?? payment.installments,
              checkout: payment.installments,
              original: installment?.original,
            })
          }

          if (!isLayout8) {
            payment?.installments.map((x) => {
              if (x.installmentNumber > installmentNumber) {
                installmentNumber = x.installmentNumber
              }
              return true
            })
          }
        }
      }

      const pay = data.paymentMethods.find(
        (pm) => pm.methodType === paymentMethodType,
      )
      if (!pay) {
        if (data.paymentMethods && data.paymentMethods.length > 0) {
          const id = data.paymentMethods[0].methodType
          setValue("payment.methodType", id)
        }
      }

      const value = NumberFunctions.round(
        quantityValue *
          NumberFunctions.round(data?.firstPurchasePrice ?? data?.value ?? 0),
      )
      const total = NumberFunctions.round(
        value +
          products.reduce(
            (acc, curr) => acc + (curr.firstPurchasePrice ?? curr.value),
            0,
          ),
      )
      const discount = NumberFunctions.round(
        voucherStatus === "valid" && voucherValue
          ? voucherValueType === TProductVoucherValue.value
            ? voucherValue
            : (total * voucherValue) / 100
          : 0,
      )

      const saleValue = total - discount
      setValue("payment.value", value)
      setValue("payment.subtotal", total)
      setValue("payment.discount", discount)
      setValue("payment.total", saleValue)

      if (paymentInstallment <= 0) {
        setValue("payment.installment", installmentNumber)
      }

      const payments = watch("payment.payments")
      if (payments.length <= 0) {
        const pay = data.paymentMethods.find(
          (pm) => pm.methodType === paymentMethodType,
        )
        if (pay) {
          setValue(
            "payment.payments",
            PaymentFunctions.getPaymentList(paymentMethodType, saleValue),
          )
        }
      }

      updateCustomCheckout(data)
      handleLoadCalculateInstallments(total - discount, true, installment)
    }
  }, [
    data,
    paymentInstallment,
    paymentMethodType,
    quantityValue,
    setValue,
    handleLoadCalculateInstallments,
  ])

  useEffect(() => {
    if (waitForApproval) {
      verifiedCheckoutCharge()
    }
  }, [verifiedCheckoutCharge, waitForApproval])

  useEffect(() => {
    window.onerror = onErrorAnalytics
    window.addEventListener(
      EVENT_LISTENER_TYPE_UNHANDLED_REJECTION,
      onEventListenerUnhandledRejection,
    )

    getProductPrice(codeId, checkoutOrderCodeId, subscriptionCodeId, areaCodeId)
    accessPageDate.current = new Date()

    if (Hosts.isHostGoAffiliatesPay()) {
      Hosts.openNavigate(
        `/${codeId}/redirect${window.location.search}`,
        navigate,
      )
      return
    }

    setPurpose(query.get("purpose"))

    const tagEventsKonduto = KondutoFunctions.addTagEventos()
    const tagCheckoutKonduto = KondutoFunctions.addTagMetaCheckout()

    const tagKonduto = TagFunctions.addScriptHeader(
      CheckoutConsts.scriptKonduto,
    )

    if (!tagInitialDataLayer.current) {
      tagInitialDataLayer.current = GoogleFunctions.initialDataLayer()
    }

    return () => {
      TagFunctions.removeMeta(tagCheckoutKonduto)
      TagFunctions.removeTags({ script: tagEventsKonduto })
      TagFunctions.removeTags({ script: tagKonduto })

      if (tagInitialDataLayer.current) {
        TagFunctions.removeTags(tagInitialDataLayer.current)
      }

      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [])

  useEffect(() => {
    const body = document.querySelector("body")
    document.removeEventListener("visibilitychange", handleVisibilityChange)
    if (exitPopup) {
      document.addEventListener("visibilitychange", handleVisibilityChange)
      body?.addEventListener("mouseleave", handleMouseleave)
    }
  }, [exitPopup])

  const saveLostSale = async () => {
    if (id.current !== "" && checkoutOrderIdValue === undefined) {
      if (emailValue !== "" || phoneValue !== "") {
        if (!lostSaleSending.current) {
          try {
            lostSaleSending.current = true

            const clientIp = await getClientIp()

            // quando o autopreenchimento está ativo no browser e o usuário utiliza
            // está chamando duas vezes o lostsale
            if (!lostSaleSending.current) {
              return
            }

            const lostSale = CheckoutPageFunctions.getLostSale(
              lostSaleCodeId.current,
              id.current,
              watch("affiliateCode"),
              nameValue,
              emailValue,
              phoneValue,
              phoneCountryValue,
              zipCodeValue,
              pageAccessIdValue,
              clientIp,
            )
            const { data: response } = await LostSaleController.insert({
              data: lostSale,
            })
            lostSaleCodeId.current = response
          } finally {
            lostSaleSending.current = false
          }
        } else {
          setTimeout(saveLostSale, 1000)
        }
      }
    }
  }

  const processCheckoutOrderFailed = () => {
    setSaleStorage(null)
    setWaitForApproval(false)
    setViewLoading(false)
    setSendingPurchase(false)
    CheckoutFunctions.addAttempts(codeId ?? "")
  }

  const validCheckoutOrderAttempts = () => {
    return CheckoutFunctions.validAttempts(codeId ?? "")
  }

  const validatePurchase = (dataSale: TSale): boolean => {
    if (!validCheckoutOrderAttempts()) {
      setError(t("errors.checkoutAttempts"))
      return false
    }

    const layout = CheckoutFunctions.getLayoutComponent(
      TCheckoutComponent.ClientsData,
      data?.checkoutComponents,
    )
    if (layout !== TCheckoutLayout.None) {
      if (!Validations.email(dataSale.email)) {
        setError(t("errors.invalidEmail"))
        setFocus("email")
        return false
      }

      if (
        !CheckoutFunctions.isLayout5ModelE(data?.checkoutComponents) &&
        !isLayout6 &&
        !isLayout7 &&
        !isLayout8
      ) {
        if (!Validations.email(dataSale.emailConfirm)) {
          setError(t("errors.invalidEmailConfirm"))
          setFocus("emailConfirm")
          return false
        }

        if (
          dataSale.email.trim().toLowerCase() !==
          dataSale.emailConfirm.trim().toLowerCase()
        ) {
          setError(t("errors.emailMismatch"))
          setFocus("email")
          return false
        }
      }

      if (!useStripePayment && !Validations.CPF_CNPJ(dataSale.document)) {
        setError(t("errors.invalidDocument"))
        setFocus("document")
        return false
      }

      const payments = watch("payment.payments")
      for (const pay of payments) {
        if ((pay.value ?? 5) < 5) {
          setError(
            `O valor mínimo para pagamento é R$ ${NumberFunctions.formatMoneyDefault(
              5,
            )}. Por favor, confira o valor do pagamento.`,
          )
          return false
        }
        if (
          !useStripePayment &&
          !pay.creditCardData?.walletToken &&
          pay.paymentMethod === TPaymentMethodEnum.CreditCard &&
          pay.creditCardData
        ) {
          const isMonthMinor = (pay.creditCardData.month ?? 0) <= 0
          const isMonthBigger = (pay.creditCardData.month ?? 0) > 12
          if (isMonthMinor || isMonthBigger) {
            setError(t("errors.cardMonthRange"))
            return false
          }
          try {
            const now = new Date()
            const nowMonth = now.getMonth() + 1
            const nowYear = Number(now.getFullYear().toString().slice(2))
            const isYearInvalid = pay.creditCardData.year < nowYear
            const isMonthInvalid =
              pay.creditCardData.month < nowMonth &&
              pay.creditCardData.year === nowYear
            if (isYearInvalid || isMonthInvalid) {
              setError(t("errors.cardExpired"))
              return false
            }
          } catch (e) {
            console.error(e)
          }
          const cvvResult = validateCvv(
            pay.creditCardData.cvv,
            pay.creditCardData.number,
          )
          if (typeof cvvResult === "string") {
            setError(cvvResult)
            return false
          }
        }
        if (
          pay.paymentMethod === TPaymentMethodEnum.DebitCard &&
          pay.debitCardData
        ) {
          const cvvResult = validateCvv(
            pay.debitCardData.cvv,
            pay.debitCardData.number,
          )
          if (typeof cvvResult === "string") {
            setError(cvvResult)
            return false
          }
        }
      }
      let valueTotal = payments
        .map((pay) => pay.value ?? 0)
        .reduce((prev, curr) => prev + curr)
      const discount = CheckoutPageFunctions.calcDiscountVoucher(
        voucherStatus,
        voucherValue,
        voucherValueType,
        valueTotal,
      )
      valueTotal -= discount
      if (valueTotal > paymentTotal) {
        setError(t("errors.valueAboveTotal"))
        return false
      }
    }

    return true
  }

  const verifiedChargedCardUpdate = useCallback(() => {
    if (isPurposeCardUpdate()) {
      const order: IOrderResponse = {
        paymentType:
          data?.paymentMethods[0].methodType ?? TPaymentMethodEnum.CreditCard,
        success: true,
        checkoutOrderId: checkoutOrderIdValue,
      }
      const charge: IOrderCharge = {
        chargeId: "",
        code: "",
        id: "",
        orderId: "",
        paymentMethod: "credit_card",
        status: IOrderChargeStatus.Paid,
        transactionId: "",
      }
      const code = codeId || data?.codeId || ""
      const sale: TSale = watch() as TSale
      const query = CheckoutFunctions.getQuerySuccessPage(
        code,
        data!,
        affiliated!,
        sale,
        order,
        onlyProducer,
        charge,
        orderBumps,
      )
      Hosts.openNavigate(`/${code}/success/redirect${query}`, navigate)
      return true
    }

    return false
  }, [codeId, data, affiliated, watch, dataResult, onlyProducer, orderBumps])

  // 3DS DESABILITADO TEMPORARIAMENTE — negociação administrativa pendente. Reativar descomentando os blocos marcados.
  /*
  const isCardPayment = (dataSale: TSale): boolean => {
    return dataSale.payment.payments.some(
      (p) =>
        p.paymentMethod === TPaymentMethodEnum.CreditCard ||
        p.paymentMethod === TPaymentMethodEnum.DebitCard,
    )
  }

  const needs3DS = (): boolean => {
    return data?.enablePagarme3DS === true
  }

  const getFirstCardForTDS = (dataSale: TSale) => {
    const pay = dataSale.payment.payments.find(
      (p) =>
        p.paymentMethod === TPaymentMethodEnum.CreditCard && p.creditCardData,
    )
    if (pay?.creditCardData) {
      return {
        number: numberOnly(pay.creditCardData.number),
        holder_name: pay.creditCardData.name.trim(),
        exp_month: pay.creditCardData.month,
        exp_year: 2000 + pay.creditCardData.year,
      }
    }
    const payDebit = dataSale.payment.payments.find(
      (p) =>
        p.paymentMethod === TPaymentMethodEnum.DebitCard && p.debitCardData,
    )
    if (payDebit?.debitCardData) {
      return {
        number: numberOnly(payDebit.debitCardData.number),
        holder_name: payDebit.debitCardData.name.trim(),
        exp_month: payDebit.debitCardData.month,
        exp_year: 2000 + payDebit.debitCardData.year,
      }
    }
    return null
  }

  const buildTDSOrderData = (dataSale: TSale): TDSOrderData | null => {
    const card = getFirstCardForTDS(dataSale)
    if (!card) return null

    const paymentValue = dataSale.payment.total ?? 0
    const amountInCents = Math.round(paymentValue * 100)

    return {
      customer: {
        name: dataSale.name.trim(),
        email: dataSale.email.trim(),
        document: numberOnly(dataSale.document),
      },
      items: [
        {
          description: data?.productName ?? "Produto",
          code: dataSale.productPriceId,
        },
      ],
      payments: [
        {
          payment_method: "credit_card",
          amount: amountInCents,
          credit_card: {
            card: {
              number: card.number,
              holder_name: card.holder_name,
              exp_month: card.exp_month,
              exp_year: card.exp_year,
              billing_address: {
                country: "BR",
                state: dataSale.address.state,
                city: dataSale.address.city,
                zip_code: numberOnly(dataSale.address.zipCode),
                line_1: `${dataSale.address.number}, ${dataSale.address.street}, ${dataSale.address.neighborhood}`,
                line_2: dataSale.address.complement,
              },
            },
          },
        },
      ],
      requestor_url: window.location.origin,
    }
  }

  const run3DSAuthentication = async (
    dataSale: TSale,
  ): Promise<boolean> => {
    const tenantId = data?.tenantId ?? ""

    // 1. Obter TDS token do backend
    const tokenResponse = await ThreeDSController.getTdsToken(tenantId)
    if (!tokenResponse) {
      setError(t("errors.threeDS.start"))
      return false
    }

    // 2. Carregar Stone 3DS SDK
    await loadStone3DSSDK(tokenResponse.environment)

    if (!window.TDS) {
      setError(t("errors.threeDS.sdk"))
      return false
    }

    // 3. Preparar containers DOM
    let methodContainer = document.getElementById("tds-method-container")
    if (!methodContainer) {
      methodContainer = document.createElement("div")
      methodContainer.id = "tds-method-container"
      methodContainer.style.display = "none"
      document.body.appendChild(methodContainer)
    }

    const challengeContainer = document.getElementById(
      "tds-challenge-container",
    )
    if (!challengeContainer) {
      setError(t("errors.threeDS.container"))
      return false
    }

    // 4. Preparar dados do pedido
    const orderData = buildTDSOrderData(dataSale)
    if (!orderData) {
      setError(t("errors.threeDS.cardData"))
      return false
    }

    // 5. Abrir dialog (mostra container de challenge)
    setThreeDSOpen(true)

    try {
      // 6. Chamar TDS.init — o SDK lida com fingerprint + challenge
      const results = await window.TDS.init(
        {
          token: tokenResponse.tdsToken,
          tds_method_container_element: methodContainer,
          challenge_container_element: challengeContainer,
          use_default_challenge_iframe_style: true,
          challenge_window_size: ChallengeWindowSize.Desktop,
        },
        orderData,
      )

      setThreeDSOpen(false)

      const authResult = results[0]
      if (!authResult) {
        setError(t("errors.threeDS.emptyResponse"))
        return false
      }

      // 7. Verificar resultado
      if (isAuthenticated(authResult)) {
        dataSale.threeDSData = mapTDSResultToOrderPayload(authResult)
        return true
      }

      if (hasFailed(authResult)) {
        setError(t("errors.threeDS.failed"))
        processCheckoutOrderFailed()
        return false
      }

      // Status U ou I — não autenticado
      setError(t("errors.threeDS.notAuthenticated"))
      return false
    } catch (e: any) {
      setThreeDSOpen(false)

      // Erro do TDS SDK
      if (e && typeof e === "object" && "getCode" in e) {
        const tdsError = e as { getCode(): string; getMessage(): string }
        setError(t("errors.threeDS.sdkError", { message: tdsError.getMessage() }))
      } else {
        const message =
          e instanceof Error
            ? e.message
            : t("errors.threeDS.cardAuth")
        setError(message)
      }
      processCheckoutOrderFailed()
      return false
    }
  }
  */

  /** Retorna `true` quando o pedido foi aceito pelo backend (sem rejeição imediata). */
  const submitOrder = async (dataSale: TSale): Promise<boolean> => {
    setSending(true)
    setSuccess(false)
    setError("")
    try {
      sendPixelsAddPaymentInfo(data, affiliated)

      const response = await OrderController.create({ data: dataSale })
      if (!response.success) {
        setError(Convertions.getErrorPayment(paymentMethodType, response))
        processCheckoutOrderFailed()
        return false
      } else {
        const order = response.data as IOrderResponse
        const status = order.status ?? ""
        if (status === "failed") {
          setSendingPurchase(false)
          setError(Convertions.getErrorPayment(paymentMethodType, response))
          processCheckoutOrderFailed()
          return false
        }

        if (verifiedChargedCardUpdate()) {
          return true
        }

        setDataResult(order)
        setValue("checkoutOrderId", order.checkoutOrderId)

        setViewLoading(true)
        setChargeId(order.chargeId)
        setWaitForApproval(true)
        return true
      }
    } catch (e) {
      setSendingPurchase(false)
      return false
    } finally {
      setSending(false)
    }
  }

  /**
   * Retorna `true` quando o pedido foi submetido ao backend sem rejeição imediata.
   * Não espera a confirmação assíncrona do pagamento (polling) — o retorno é usado
   * apenas pelo `ev.complete()` da folha de pagamento das wallets.
   */
  const Submit = async (dataSale: TSale): Promise<boolean> => {
    setSendingPurchase(true)

    if (!validatePurchase(dataSale)) {
      setSendingPurchase(false)
      return false
    }

    setSaleStorage(null)
    dataSale.productPriceId = id.current
    dataSale.lostSaleCode = lostSaleCodeId.current
    dataSale.checkoutModelId = data?.checkoutModelId
    dataSale.kondutoId = localStorage.getItem("visitorID") || ""
    dataSale.fingerprintId = fingerprintRef.current

    const isRecaptcha = CheckoutFunctions.askReCAPTCHA(
      data,
      dataSale,
      accessPageDate.current,
    )
    if (isRecaptcha) {
      if (executeRecaptcha) {
        const token = await executeRecaptcha("checkout")
        dataSale.recaptchaToken = token
        handleRecaptchaChange(token)
      }
    } else {
      dataSale.recaptchaToken = null
    }

    // 3DS DESABILITADO TEMPORARIAMENTE — negociação administrativa pendente. Reativar descomentando os blocos marcados.
    // Fluxo 3DS via Stone SDK — autenticar ANTES de criar o pedido
    /*
    if (needs3DS() && isCardPayment(dataSale)) {
      setSending(true)
      setError("")
      try {
        const success = await run3DSAuthentication(dataSale)
        if (!success) {
          setSendingPurchase(false)
          setSending(false)
          return
        }
      } catch (e) {
        const message =
          e instanceof Error ? e.message : t("errors.threeDS.cardAuth")
        setError(message)
        setSendingPurchase(false)
        setSending(false)
        return
      }
    }
    */

    // Stripe tokenization for international buyers
    const firstCardPayment = dataSale.payment.payments.find(
      (p) => p.paymentMethod === TPaymentMethodEnum.CreditCard && p.creditCardData
    )
    // Pagamento por wallet já traz o token pronto (walletToken): não há CardElement
    // preenchido pra tokenizar, e `cardId` + `walletToken` juntos são rejeitados pelo
    // backend (mutuamente exclusivos — ver spec, seção 4.2). Ver finding C2.
    const isWalletPayment = !!firstCardPayment?.creditCardData?.walletToken
    if (useStripePayment && stripeFormRef.current && !isWalletPayment) {
      const tokenResult = await stripeFormRef.current.tokenize()
      if ('error' in tokenResult) {
        setError(tokenResult.error)
        setSendingPurchase(false)
        return false
      }
      // Inject Stripe payment method ID into the first credit card payment
      if (firstCardPayment?.creditCardData) {
        firstCardPayment.creditCardData.cardId = tokenResult.paymentMethodId
        firstCardPayment.creditCardData.installments = 1
      }
    }
    dataSale.buyerCountry = buyerCountry

    // Criar pedido
    return await submitOrder(dataSale)
  }

  // `Submit` é recriado a cada render; guardar em ref permite memoizar
  // `onWalletAuthorized` (senão o GatewayContext muda de identidade a cada render e
  // o PaymentRequest do Stripe é reconstruído sem parar). Ver finding I5.
  const submitRef = useRef(Submit)
  submitRef.current = Submit

  const onWalletAuthorized = useCallback(
    async (result: TWalletAuthorizedResult): Promise<boolean> => {
      if ("error" in result) {
        setError(result.error)
        return false
      }

      // flushSync: os campos de cartão digitado só deixam de ser obrigatórios depois
      // que o React re-renderiza com o walletToken preenchido. Sem isso o handleSubmit
      // abaixo valida com as regras antigas e nunca chega no Submit. Ver finding C1.
      flushSync(() => {
        setValue("payment.methodType", TPaymentMethodEnum.CreditCard)
        setValue(
          "payment.payments.0.paymentMethod",
          TPaymentMethodEnum.CreditCard,
        )
        setValue(
          "payment.payments.0.creditCardData.walletProvider",
          result.walletProvider,
        )
        setValue(
          "payment.payments.0.creditCardData.walletToken",
          result.walletToken,
        )
        setValue(
          "payment.payments.0.creditCardData.walletCardMetadata",
          result.walletCardMetadata,
        )
        setValue("payment.installment", 1)
      })

      // O handleSubmit do react-hook-form descarta o retorno do callback, então o
      // resultado do Submit é capturado por fora. Ver finding C2.
      let submitted = false
      await handleSubmit(async (dataSale) => {
        submitted = await submitRef.current(dataSale)
      })()
      return submitted
    },
    [handleSubmit, setValue],
  )

  const nextInput = () => {
    if (!disableFocus.current) {
      const inputs = Array.prototype.slice.call(
        document.querySelectorAll("input"),
      )
      const currInput = document.activeElement
      const currInputIndex = inputs.indexOf(currInput)
      const nextinputIndex = (currInputIndex + 1) % inputs.length
      const input = inputs[nextinputIndex]
      if (input) {
        input.focus()
      }
    }
  }

  const handleFormKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === "Enter") {
      if (!fieldSetFocus.current) {
        nextInput()
      }
      fieldSetFocus.current = false
      e.preventDefault()
    }
  }

  const handleAddresNumberKeyDown = (
    e: React.KeyboardEvent<HTMLDivElement>,
  ) => {
    if (e.key === "Enter") {
      fieldSetFocus.current = true
      const neighborhoodValue = watch("address.neighborhood")
      if (neighborhoodValue === "") {
        setFocus("address.neighborhood")
      } else {
        setFocus("address.complement")
      }
      e.preventDefault()
    }
  }

  const handleAddresComplementKeyDown = (
    e: React.KeyboardEvent<HTMLDivElement>,
  ) => {
    if (e.key === "Enter") {
      fieldSetFocus.current = true
      if (
        paymentMethodType === TPaymentMethodEnum.CreditCard ||
        paymentMethodType === TPaymentMethodEnum.DebitCard
      ) {
        if (isLayout6 || isLayout7 || isLayout8) {
          setFocus("payment.payments.0.creditCardData.number")
        } else {
          setFocus("payment.payments.0.creditCardData.name")
        }
      }
      e.preventDefault()
    }
  }

  const handleApplyVoucher = () => {
    sendApplyVoucher(voucherIdentifier)
  }

  const handleRemoveVoucher = () => {
    voucherType.current = "voucher"
    setValue("payment.voucherIdentifier", null)
    setValue("payment.voucherValue", null)
    setValue("payment.voucherStatus", "none")
  }

  const handleRecaptchaChange = (token: string | null) => {
    setValue("recaptchaToken", token)
  }

  useEffect(() => {
    if (areaCodeIdValue) {
      getProductPrice(undefined, undefined, undefined, areaCodeIdValue)
    }
  }, [areaCodeIdValue])

  if (loading) return <CheckoutLoading />

  if (errorPrice !== "") return <CheckoutError />

  if (stripeConfigError && useStripePayment) return <CheckoutError />

  const sxMain = CheckoutPageFunctions.getCheckoutStyleMain(
    data,
    checkoutSettings,
    list,
  )

  const sxContainer = CheckoutPageFunctions.getCheckoutStyleContainer(
    data,
    list,
  )

  return (
    <Box sx={sxMain}>
      <Container
        component="main"
        maxWidth={false}
        disableGutters={isNewLayoutCoverImage}
        sx={sxContainer}
      >
        <GatewayProvider
          buyerCountry={buyerCountry}
          setBuyerCountry={setBuyerCountry}
          useStripe={useStripePayment}
          stripeFormRef={stripeFormRef}
          onConfigError={() => setStripeConfigError(true)}
          onWalletAuthorized={onWalletAuthorized}
        >
          <FormProvider {...methods}>
            <CheckoutComponenetList
              key="CheckoutComponenetList"
              list={list}
              id={id}
              loading={loading}
              loadingVoucher={loadingVoucher}
              isCheckoutBuilder={list.length > 1 || list[0].children.length > 0}
              searchedZipCode={searchedZipCode}
              data={data}
              control={control}
              errors={errors}
              productInstallments={installment?.original}
              installments={installment?.checkout}
              orderBumps={orderBumps}
              products={products}
              paymentInstallment={paymentInstallment}
              paymentMethodType={paymentMethodType}
              paymentTotal={paymentTotal}
              paymentValue={paymentValue}
              watch={watch}
              setValue={setValue}
              onSubmit={handleSubmit(Submit)}
              onKeyDown={handleFormKeyDown}
              saveLostSale={saveLostSale}
              clearErrors={clearErrors}
              searchZipCode={searchZipCode}
              handleAddresComplementKeyDown={handleAddresComplementKeyDown}
              handleAddresNumberKeyDown={handleAddresNumberKeyDown}
              orderBumpChange={orderBumpChange}
              setOpenTermsPurchase={setOpenTermsPurchase}
              onSendPixel={sendPixelsInitiateCheckoutData}
              onApplyVoucher={handleApplyVoucher}
              onRemoveVoucher={handleRemoveVoucher}
            />
          </FormProvider>
        </GatewayProvider>
      </Container>

      <InfoDialog
        open={openZipCodeInvalid}
        onClose={() => setOpenZipCodeInvalid(false)}
        title={t("errors.zipNotFoundTitle")}
        text={t("errors.zipNotFoundText")}
        dataCyButton="zipCodeNotFound"
      />

      <TermsPurchaseDialog
        open={openTermsPurchase}
        onClose={() => setOpenTermsPurchase(false)}
      />

      <ExitPopupDialog
        isExitPopup={isExitPopup}
        exitPopup={exitPopup}
        setIsExitPopup={setIsExitPopup}
        applyVoucher={applyVoucher}
      />

      {/* 3DS DESABILITADO TEMPORARIAMENTE — negociação administrativa pendente. Reativar descomentando os blocos marcados. */}
      {/*
      <ThreeDSDialog
        open={threeDSOpen}
        onClose={() => {
          setThreeDSOpen(false)
        }}
      />
      */}

      <LoadingBackdrop open={sending} />

      <CheckoutSendingBackdrop
        open={viewLoading}
        paymentMethod={paymentMethodType}
      />

      <ErrorSnackbar open={error !== ""} onClose={() => setError("")}>
        {error}
      </ErrorSnackbar>

      <Snackbar
        id="errorVoucher"
        open={errorVoucher !== ""}
        autoHideDuration={2000}
        onClose={() => setErrorVoucher("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setErrorVoucher("")}
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorVoucher}
        </Alert>
      </Snackbar>

      <Snackbar
        id="successVoucher"
        open={successVoucher}
        autoHideDuration={2000}
        onClose={() => setSuccessVoucher(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSuccessVoucher(false)}
          sx={{ width: "100%" }}
        >
          Você recebeu um{" "}
          {voucherType.current === "discount" ? "desconto" : "cupom"} de{" "}
          {watch("payment.voucherType") === TProductVoucherValue.value
            ? NumberFunctions.formatMoneyDefault(watch("payment.voucherValue"))
            : NumberFunctions.toPercentage(watch("payment.voucherValue"))}
        </Alert>
      </Snackbar>

      <Snackbar
        open={success}
        autoHideDuration={4000}
        onClose={() => setSuccess(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSuccess(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          Gerado com sucesso
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default CheckoutPage
